import { useState } from 'react';
import { CaretDownIcon, CaretRightIcon, PlusIcon } from '@phosphor-icons/react';
import { DateTime, Num, Plural, T, Var, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { WorkbookItemIcon } from './workbookItemIcon';
import { WorkbookSchemaGroupSummary } from './WorkbookSchemaGroupSummary';
import type { SchemaGroupReading } from './workbookSchemaGroup';
import { workbookItemIconKind } from './workbookItemIconKind';
import { useWorkbookProcess } from './useWorkbookProcess';
import { WorkbookCellInstructions } from './WorkbookProcessCell';
import {
  truncatedNames,
  workbookDataItems,
  workbookFileSinks,
  type WorkbookDataContext,
  type WorkbookDataItem,
  type WorkbookDataOrigin,
  type WorkbookDataReader,
} from './workbookDataItems';
import { readPersistedFlag, writePersistedFlag } from '@/lib/persistedFlag';

/** Whether this viewer last left the Data section open; closed when unknown. */
const OPEN_KEY = 'workbooks.process.dataOpen';
const UPLOADED_FORMAT = { dateStyle: 'medium', timeStyle: 'short' } as const;

/**
 * The Process tab's collapsible Data section: every File and Source the
 * Worksheets read, where it comes from, its known shape, who reads it and its
 * Data notes, then the xlsx files the File Outputs write.
 */
export function WorkbookDataSection({
  context,
}: {
  context: WorkbookDataContext;
}) {
  const gt = useGT();
  const { definition, openItem } = useWorkbookProcess();
  const [open, setOpen] = useState(() => readPersistedFlag(OPEN_KEY));
  const items = workbookDataItems(definition, context);
  const sinks = workbookFileSinks(definition);
  if (!items.length && !sinks.length) return null;
  return (
    <Collapsible
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        writePersistedFlag(OPEN_KEY, next);
      }}
      className="rounded-lg border border-input bg-card"
    >
      <h3>
        <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-ring">
          {open ? (
            <CaretDownIcon className="size-4 text-muted-foreground" />
          ) : (
            <CaretRightIcon className="size-4 text-muted-foreground" />
          )}
          <T context="workbook process data section heading">Data</T>
          <span className="text-xs font-normal text-muted-foreground">
            <T context="workbook process data section count">
              <Num>{items.length}</Num>{' '}
              <Plural n={items.length} one="input" other="inputs" />
            </T>
          </span>
        </CollapsibleTrigger>
      </h3>
      <CollapsibleContent>
        <ul
          className="flex flex-col divide-y divide-input border-t border-input"
          aria-label={gt('Data', {
            context: 'workbook process data section heading',
          })}
        >
          {items.map((item) => (
            <WorkbookDataEntry key={item.id} item={item} />
          ))}
          {sinks.map((sink) => (
            <li
              key={sink.itemId}
              className="flex min-w-0 flex-col gap-1 px-3 py-3"
            >
              <WorkbookDataTitle
                id={sink.itemId}
                name={sink.fileName}
                open={openItem}
              />
              <p className="pl-6 text-xs text-muted-foreground">
                <T context="workbook process data sink">
                  xlsx export · from <Var>{sink.sheetNames.join(', ')}</Var>
                </T>
              </p>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

function WorkbookDataTitle({
  id,
  name,
  open,
}: {
  id: string;
  name: string;
  open: (id: string) => void;
}) {
  const gt = useGT();
  const { definition } = useWorkbookProcess();
  return (
    <button
      type="button"
      className="flex min-w-0 items-center gap-2 self-start rounded text-left text-sm font-medium hover:underline focus-visible:outline-2 focus-visible:outline-ring"
      onClick={() => open(id)}
      aria-label={gt('Open {name}', {
        context: 'workbook open execution item',
        name,
      })}
    >
      <WorkbookItemIcon
        kind={workbookItemIconKind(definition, id) ?? 'data'}
        className="size-4 shrink-0 text-muted-foreground"
        aria-hidden
      />
      <span className="truncate">{name}</span>
    </button>
  );
}

function WorkbookDataEntry({ item }: { item: WorkbookDataItem }) {
  const gt = useGT();
  const { outline, openItem, canUpdate, generation, insert } =
    useWorkbookProcess();
  const notes = outline.dataNotes(item.id);
  return (
    <li
      id={`process-node-${item.id}`}
      className="flex min-w-0 scroll-mt-3 flex-col gap-1 px-3 py-3"
    >
      <WorkbookDataTitle
        id={item.id}
        name={
          (item.origin.kind === 'schema-group' &&
            item.origin.reading.schemaName) ||
          item.name
        }
        open={openItem}
      />
      {item.origin.kind === 'schema-group' && (
        <WorkbookSchemaGroupMembers reading={item.origin.reading} />
      )}
      <div className="flex flex-col gap-0.5 pl-6 text-xs text-muted-foreground">
        <WorkbookDataOriginText origin={item.origin} />
        <WorkbookDataShape item={item} />
        {item.readBy.length > 0 && (
          <WorkbookDataReaders readers={item.readBy} open={openItem} />
        )}
      </div>
      {notes.length > 0 && (
        <div className="flex flex-col gap-3 pt-2 pl-6">
          {notes.map((cell) => (
            <div
              key={cell.head.id}
              id={`process-node-${cell.head.id}`}
              className="flex min-w-0 scroll-mt-3 flex-col gap-2"
            >
              <WorkbookCellInstructions cell={cell} showHeadTitle />
            </div>
          ))}
        </div>
      )}
      {canUpdate && !outline.full && (
        <Button
          size="sm"
          variant="ghost"
          className="ml-5 h-7 self-start"
          disabled={generation.generating}
          onClick={() => insert(null, undefined, [item.id])}
          aria-label={gt('Add data note to {name}', {
            context: 'workbook add data note',
            name: item.name,
          })}
        >
          <PlusIcon />
          <T context="workbook add data note">Add note</T>
        </Button>
      )}
    </li>
  );
}

function WorkbookDataOriginText({ origin }: { origin: WorkbookDataOrigin }) {
  switch (origin.kind) {
    case 'file':
      return <WorkbookFileOriginText origin={origin} />;
    case 'file-sheet':
      return <WorkbookFileSheetOriginText origin={origin} />;
    case 'data-asset':
      return <WorkbookDataAssetOriginText origin={origin} />;
    case 'entity':
      return <WorkbookEntityOriginText origin={origin} />;
    default:
      return <WorkbookPlainOriginText origin={origin} />;
  }
}

type WorkbookPlainOrigin = Extract<
  WorkbookDataOrigin,
  {
    kind:
      | 'pasted'
      | 'mapping'
      | 'stateful-table'
      | 'data-source'
      | 'schema-group'
      | 'object';
  }
>;

function WorkbookPlainOriginText({ origin }: { origin: WorkbookPlainOrigin }) {
  switch (origin.kind) {
    case 'pasted':
      return (
        <p>
          <T context="workbook data pasted origin">Pasted table</T>
        </p>
      );
    case 'mapping':
      return (
        <p>
          <T context="workbook data mapping origin">
            Data mapping · <Var>{origin.name}</Var>
          </T>
        </p>
      );
    case 'stateful-table':
      return (
        <p>
          <T context="workbook data stateful table origin">
            Stateful table · <Var>{origin.name}</Var>
          </T>
        </p>
      );
    case 'data-source':
      return (
        <p>
          <T context="workbook data link origin">
            Data source · <Var>{origin.name}</Var>
          </T>
        </p>
      );
    case 'schema-group':
      return <WorkbookSchemaGroupOriginText origin={origin} />;
    case 'object':
      return (
        <p>
          <T context="workbook data object origin">
            Gently · <Var>{origin.object}</Var>
          </T>
        </p>
      );
  }
}

/** The data sources a combined table reads, indented below its schema name. */
/** How many of a group's members show before "Show all". */
const MEMBERS_SHOWN = 5;

/** A group's members by name; a long list shows its first few until expanded. */
function WorkbookSchemaGroupMembers({
  reading,
}: {
  reading: SchemaGroupReading;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!reading.resolved || !reading.members.length) return null;
  const total = reading.members.length;
  // Hiding just one saves no room, so a list one over the limit shows whole.
  const collapsible = total > MEMBERS_SHOWN + 1;
  const shown =
    collapsible && !expanded
      ? reading.members.slice(0, MEMBERS_SHOWN)
      : reading.members;
  return (
    <div className="ml-6 flex flex-col items-start gap-1 border-l border-input pl-3 text-xs">
      <ul className="flex w-full min-w-0 flex-col gap-0.5">
        {shown.map((name, index) => (
          <li key={reading.memberIds[index]} className="truncate">
            {name}
          </li>
        ))}
      </ul>
      {collapsible && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          className="rounded text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring"
        >
          {expanded ? (
            <T context="workbook data group members collapse">Show fewer</T>
          ) : (
            <T context="workbook data group members expand">
              Show all <Num>{total}</Num>
            </T>
          )}
        </button>
      )}
    </div>
  );
}

function WorkbookSchemaGroupOriginText({
  origin,
}: {
  origin: Extract<WorkbookDataOrigin, { kind: 'schema-group' }>;
}) {
  // Without the schema's membership (no Data Source read) only its name shows.
  if (!origin.reading.resolved) return null;
  return (
    <p>
      <WorkbookSchemaGroupSummary reading={origin.reading} />
    </p>
  );
}

/** An xlsx File: its sheets, and when it was uploaded if known. */
function WorkbookFileOriginText({
  origin,
}: {
  origin: Extract<WorkbookDataOrigin, { kind: 'file' }>;
}) {
  return (
    <p>
      <T context="workbook data file origin">
        xlsx file · <Var>{origin.fileName}</Var> · sheets:{' '}
        <Var>{origin.sheets.join(', ')}</Var>
      </T>
      {origin.uploadedAt && (
        <>
          {' · '}
          <T context="workbook data file uploaded">
            uploaded{' '}
            <DateTime options={UPLOADED_FORMAT}>
              {new Date(origin.uploadedAt)}
            </DateTime>
          </T>
        </>
      )}
    </p>
  );
}

/** A Source reading one sheet of a File (a dash once the File was removed). */
function WorkbookFileSheetOriginText({
  origin,
}: {
  origin: Extract<WorkbookDataOrigin, { kind: 'file-sheet' }>;
}) {
  return (
    <p>
      <T context="workbook data file sheet origin">
        xlsx file · <Var>{origin.fileName ?? '—'}</Var> · sheet{' '}
        <Var>{origin.sheetName}</Var> · headers on row{' '}
        <Var>{origin.headerRow}</Var>
      </T>
    </p>
  );
}

/** A Data Asset, at its Latest or a pinned version. */
function WorkbookDataAssetOriginText({
  origin,
}: {
  origin: Extract<WorkbookDataOrigin, { kind: 'data-asset' }>;
}) {
  return (
    <p>
      {origin.pinned ? (
        <T context="workbook data asset origin">
          Data asset · <Var>{origin.name}</Var> · pinned version
        </T>
      ) : (
        <T context="workbook data asset origin">
          Data asset · <Var>{origin.name}</Var> · latest version
        </T>
      )}
    </p>
  );
}

/** An Entity, with how many filters narrow it. */
function WorkbookEntityOriginText({
  origin,
}: {
  origin: Extract<WorkbookDataOrigin, { kind: 'entity' }>;
}) {
  return (
    <p>
      {origin.filterCount ? (
        <T context="workbook data entity origin">
          Entity · <Var>{origin.name}</Var> · <Num>{origin.filterCount}</Num>{' '}
          <Plural n={origin.filterCount} one="filter" other="filters" />
        </T>
      ) : (
        <T context="workbook data entity origin">
          Entity · <Var>{origin.name}</Var> · every row
        </T>
      )}
    </p>
  );
}

function WorkbookDataShape({ item }: { item: WorkbookDataItem }) {
  if (!item.columns) return null;
  const { shown, more } = truncatedNames(item.columns);
  return (
    <p title={item.columns.join(', ')}>
      <T context="workbook data columns">
        <Num>{item.columns.length}</Num>{' '}
        <Plural n={item.columns.length} one="column" other="columns" />:{' '}
        <Var>{shown}</Var>
      </T>
      {more > 0 && (
        <T context="workbook data more columns">
          {' '}
          and <Num>{more}</Num> more
        </T>
      )}
      {item.rowCount !== undefined && (
        <>
          {' · '}
          <T context="workbook data row count">
            <Num>{item.rowCount}</Num>{' '}
            <Plural n={item.rowCount} one="row" other="rows" />
          </T>
        </>
      )}
    </p>
  );
}

export function WorkbookDataReaders({
  readers,
  open,
}: {
  readers: readonly WorkbookDataReader[];
  open: (id: string) => void;
}) {
  const gt = useGT();
  const verb = (via: WorkbookDataReader['via']) =>
    via === 'source'
      ? gt('Read as', { context: 'workbook data reader' })
      : via === 'lookup'
        ? gt('Looked up by', { context: 'workbook data reader' })
        : gt('Read by', { context: 'workbook data reader' });
  // One verb per kind of reader: "Read by Products 1, Products 2".
  const groups: {
    via: WorkbookDataReader['via'];
    readers: WorkbookDataReader[];
  }[] = [];
  for (const reader of readers) {
    const group = groups.find((entry) => entry.via === reader.via);
    if (group) group.readers.push(reader);
    else groups.push({ via: reader.via, readers: [reader] });
  }
  return (
    <p className="flex flex-wrap items-center gap-x-2">
      {groups.map((group) => (
        <span key={group.via}>
          {verb(group.via)}{' '}
          {group.readers.map((reader, index) => (
            <span key={reader.id}>
              {index > 0 && ', '}
              <button
                type="button"
                className="rounded text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                onClick={() => open(reader.id)}
              >
                {reader.name}
              </button>
            </span>
          ))}
        </span>
      ))}
    </p>
  );
}
